// Head pose (yaw / pitch / roll, in degrees) from MediaPipe's facial
// transformation matrix. Used by the framing gate to reject faces that are
// turned away from the camera — the BE touch-up and compositing assume a
// roughly front-facing head.
//
// `outputFacialTransformationMatrixes: true` (see landmarker.ts) makes
// detectForVideo return one 4×4 Matrix per face: the rigid transform from the
// canonical face model into camera space. Its upper-left 3×3 is the head
// rotation; we extract Tait-Bryan angles from it.
//
// LAYOUT ASSUMPTION: the flat `data` array is treated as COLUMN-MAJOR, so the
// element at (row, col) is data[col*4 + row]. If a future MediaPipe build ships
// row-major data this decomposition transposes and yaw/pitch swap — the
// cam-debug overlay prints matrix-roll next to the independent eye-corner tilt
// so a mismatch is obvious (they should track each other when the head rolls).
//
// SIGN is not relied upon: the gate compares |yaw| / |pitch| to thresholds, so a
// flipped axis sign is harmless. Only a row/column transpose would matter.

import type { Matrix } from "@mediapipe/tasks-vision";

export type HeadPose = { yaw: number; pitch: number; roll: number };

const RAD2DEG = 180 / Math.PI;

export function headPoseFromMatrix(matrix: Matrix | undefined): HeadPose | null {
  if (!matrix || matrix.data.length < 16) return null;
  const m = matrix.data;
  // R[row][col] = m[col*4 + row] (column-major).
  const r00 = m[0];
  const r10 = m[1];
  const r20 = m[2];
  const r21 = m[6];
  const r22 = m[10];

  const pitch = Math.atan2(r21, r22) * RAD2DEG;          // rotation about X (nod)
  const yaw = Math.atan2(-r20, Math.hypot(r21, r22)) * RAD2DEG; // about Y (turn)
  const roll = Math.atan2(r10, r00) * RAD2DEG;           // about Z (tilt)
  return { yaw, pitch, roll };
}
